// ModalDesyncGar.jsx
import React, { useState } from 'react';
import { X, Unlink, AlertTriangle, UserX, Shield } from 'lucide-react';
import { usePostApi } from '../../hooks/post_api_hook/usePostApi';
import { url } from '../../utils/confserveur';

const ModalDesyncGar = ({ show, handleClose, student, onDesyncSuccess }) => {
    const [{ isLoading }, doPost] = usePostApi();
    const [confirmationStep, setConfirmationStep] = useState(false);

    if (!show || !student) return null;

   
    const handleDesync = async () => {
        if (!confirmationStep) {
            setConfirmationStep(true);
            return;
        }

        try {
            const result = await doPost(
                {}, // Pas de données nécessaires
                `${url}eleves/${student.id}/desynchroniser_eleve_gar/`
            );

            if (result.success) {
              
                alert(`✅ ${student.prenom} ${student.nom} a été désynchronisé avec succès !`);
                
                // Callback pour rafraîchir la liste
                if (onDesyncSuccess) {
                    onDesyncSuccess(student.id);
                }
                
                handleCloseModal();
            } else {
                alert(`❌ Erreur : ${result.error?.response?.data?.error || 'Erreur inconnue'}`);
            }
        } catch (error) {
            console.error('❌ Erreur:', error);
            alert('Une erreur inattendue s\'est produite.');
        }
    };

    const handleCloseModal = () => {
        setConfirmationStep(false);
        handleClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" 
             style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)' }}>
            <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl animate-slideUp flex flex-col max-h-[90vh]">
                
                {/* Header */}
                <div className="relative overflow-hidden flex-shrink-0"
                     style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                    <div className="absolute inset-0 opacity-10" style={{ 
                        background: 'radial-gradient(circle at top right, white 0%, transparent 70%)'
                    }}></div>
                    <div className="relative z-10 px-5 py-4 sm:px-6 sm:py-5 text-white">
                        <h1 className="text-lg sm:text-xl font-semibold mb-0.5">Désynchronisation ENT</h1>
                        <p className="opacity-90 text-xs sm:text-sm">
                            {confirmationStep ? 'Confirmer la désynchronisation' : 'Gérer la connexion ENT de l\'élève'}
                        </p>
                    </div>
                    <button 
                        onClick={handleCloseModal}
                        className="absolute z-20 top-4 right-4 w-8 h-8 rounded-lg bg-white/20 backdrop-blur
                                   flex items-center justify-center text-white hover:bg-white/30 transition-all"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
                    {!confirmationStep ? (
                        <>
                            {/* Student Info Card */}
                            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-3 sm:p-4 mb-3 sm:mb-4 border border-gray-200">
                                <div className="flex items-center gap-3 sm:gap-4">
                                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600
                                                  flex items-center justify-center text-white font-bold text-lg sm:text-xl shadow-lg flex-shrink-0">
                                        {student.initials}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-0.5 break-words">
                                            {student.prenom} {student.nom}
                                        </h2>
                                        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                                            <Shield size={14} className="text-green-600" />
                                            <span>Synchronisé avec l'ENT</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* GAR Info */}
                            <div className="bg-white rounded-xl border-2 border-gray-200 p-3 sm:p-4 mb-3 sm:mb-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0"></div>
                                    Informations de l'élève après désynchronisation ENT
                                </h3>
                                <div className="space-y-1 text-xs sm:text-sm">
                                    <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                                        <span className="text-gray-600">Nom : </span>
                                        <span className="font-mono text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                                            {student.eleve_temp_nom || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                                        <span className="text-gray-600">Prénom : </span>
                                        <span className="font-mono text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                                            {student.eleve_temp_prenom || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-1.5">
                                        <span className="text-gray-600">Statut</span>
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                                                       bg-green-100 text-green-700 text-xs font-medium">
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            Actif
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Warning */}
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                                <div className="flex items-start gap-2 sm:gap-3">
                                    <AlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={18} />
                                    <div className="flex-1">
                                        <h4 className="text-sm font-semibold text-amber-800 mb-0.5">Attention</h4>
                                        <p className="text-amber-700 text-xs sm:text-sm">
                                            La désynchronisation empêchera l'élève d'accéder à vos quiz sur TanoEd.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        /* Confirmation Step */
                        <div className="text-center py-6">
                            <div className="inline-flex items-center justify-center w-16 h-16
                                          bg-red-100 rounded-full mb-4">
                                <UserX className="w-8 h-8 text-red-600" />
                            </div>
                            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                                Êtes-vous sûr ?
                            </h3>
                            <p className="text-gray-600 text-sm mb-2">
                                Vous allez désynchroniser <strong>{student.prenom} {student.nom}</strong> de l'ENT.
                            </p>
                            <p className="text-sm text-gray-500">
                                Cette action peut être annulée en resynchronisant l'élève ultérieurement.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 sm:px-6 sm:py-4 gap-3 bg-gray-50 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between flex-shrink-0">
                    <button
                        onClick={handleCloseModal}
                        className="w-full sm:w-auto text-center px-4 sm:px-6 py-2.5 text-sm sm:text-base bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold
                                 hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/50"
                    >
                        Annuler
                    </button>
                    
                    <button 
                        onClick={handleDesync}
                        disabled={isLoading}
                        className={`w-full sm:w-auto justify-center px-4 sm:px-6 py-2.5 rounded-xl text-sm sm:text-base font-semibold transition-all
                                  inline-flex items-center gap-2 ${
                            confirmationStep 
                                ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:shadow-lg' 
                                : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:shadow-lg'
                        } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'transform hover:-translate-y-0.5'}`}
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Désynchronisation...
                            </>
                        ) : confirmationStep ? (
                            <>
                                <Unlink size={18} />
                                Confirmer la désynchronisation
                            </>
                        ) : (
                            <>
                                <UserX size={18} />
                                Désynchroniser cet élève
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Loading overlay */}
            {isLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
                        <p className="text-gray-700 font-medium">Désynchronisation en cours...</p>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-slideUp {
                    animation: slideUp 0.4s ease;
                }
            `}</style>
        </div>
    );
};

export default ModalDesyncGar;